import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List as BulletIcon,
  ListOrdered as OrderedIcon,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Pilcrow,
  Strikethrough,
  Quote,
  Minus,
} from "lucide-react";

/**
 * RichTextEditor — TipTap-based WYSIWYG used in the admin product form.
 * Emits HTML on every change via onChange(html).
 * Pass `value` (HTML string) to initialize / reset content.
 */
export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "text-sage underline hover:text-sage-dark" },
      }),
      Placeholder.configure({
        placeholder:
          placeholder ||
          "Write the product description… use the toolbar to add headings, bold, lists.",
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[220px] px-4 py-3 text-ink",
        "data-testid": testId || "rich-text-editor-area",
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Treat the empty editor as truly empty (TipTap returns "<p></p>")
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g. opening a different product)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current && next !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(next || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL (leave empty to remove link)", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-border-subtle bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border-subtle bg-cream-warm px-2 py-1.5">
        <ToolButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Bold (Ctrl+B)" testId="rte-bold">
          <BoldIcon size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Italic (Ctrl+I)" testId="rte-italic">
          <ItalicIcon size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} label="Underline (Ctrl+U)" testId="rte-underline">
          <UnderlineIcon size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} label="Strikethrough" testId="rte-strike">
          <Strikethrough size={14} />
        </ToolButton>
        <Divider />
        <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Heading" testId="rte-h2">
          <Heading2 size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Subheading" testId="rte-h3">
          <Heading3 size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} label="Paragraph" testId="rte-paragraph">
          <Pilcrow size={14} />
        </ToolButton>
        <Divider />
        <ToolButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Bullet list" testId="rte-bullet">
          <BulletIcon size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Numbered list" testId="rte-ordered">
          <OrderedIcon size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Quote" testId="rte-quote">
          <Quote size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().setHorizontalRule().run()} label="Divider" testId="rte-hr">
          <Minus size={14} />
        </ToolButton>
        <Divider />
        <ToolButton onClick={promptLink} active={editor.isActive("link")} label="Insert link" testId="rte-link">
          <LinkIcon size={14} />
        </ToolButton>
        <Divider />
        <ToolButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} label="Undo" testId="rte-undo">
          <Undo2 size={14} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} label="Redo" testId="rte-redo">
          <Redo2 size={14} />
        </ToolButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolButton({ children, onClick, active, disabled, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`p-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        active
          ? "bg-sage text-cream"
          : "text-ink-soft hover:bg-cream hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-4 w-px bg-border-subtle" aria-hidden />;
}
